import Vue from 'vue';
import moment from 'moment';
import { notification } from 'ant-design-vue';
import ProjectPreorderDraftAPI, { ProjectPreorderDraft } from '@/api/ProjectPreorderDraftAPI';

type ProjectKey = string | number | null | undefined;

const state = Vue.observable({ drafts: {} as Record<string, ProjectPreorderDraft | null> });

function toKey(projectId: ProjectKey): string {
    return projectId === null || projectId === undefined ? '' : String(projectId);
}

export function getPreorderDraft(projectId: ProjectKey): ProjectPreorderDraft | null {
    return state.drafts[toKey(projectId)] ?? null;
}

export function setPreorderDraft(projectId: ProjectKey, draft: ProjectPreorderDraft | null): void {
    Vue.set(state.drafts, toKey(projectId), draft);
}

export async function fetchPreorderDraft(projectId: ProjectKey): Promise<ProjectPreorderDraft | null> {
    if (!toKey(projectId)) {
        return null;
    }

    try {
        const data = await ProjectPreorderDraftAPI.get(toKey(projectId));
        setPreorderDraft(projectId, data.draft);
        return data.draft;
    } catch (e) {
        console.error('Could not load the order without configuration draft', e);
        return getPreorderDraft(projectId);
    }
}

export async function removePreorderDraft(vm: Vue, projectId: ProjectKey): Promise<boolean> {
    try {
        await ProjectPreorderDraftAPI.remove(toKey(projectId));
    } catch (e) {
        notification.error({
            message: vm.$t('Dogodila se greška') as string,
            description: (e as Error).message,
        });
        return false;
    }

    setPreorderDraft(projectId, null);
    return true;
}

export function formatPreorderDraftSavedAt(draft: ProjectPreorderDraft): string {
    return moment(draft.savedAt).format('DD.MM.YYYY HH:mm');
}

export function confirmDeletePreorderDraft(vm: Vue): Promise<boolean> {
    return new Promise((resolve) => {
        vm.$confirm({
            title: vm.$t('Delete the draft?') as string,
            content: vm.$t(
                'The draft of the order without configuration will be deleted. The project connection, comment and attachments stay in the project. The desired delivery date, the positions and their attachments will be lost.',
            ) as string,
            okText: vm.$t('Delete draft') as string,
            okType: 'danger',
            cancelText: vm.$t('Cancel') as string,
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
        });
    });
}

export function confirmReplacePreorderDraftWithOrder(vm: Vue): Promise<boolean> {
    return new Promise((resolve) => {
        vm.$confirm({
            title: vm.$t('The draft of the order without configuration will be deleted') as string,
            content: vm.$t(
                'If you continue and add positions, we will delete the draft of the order without configuration. The project connection, comment and attachments stay in the project.',
            ) as string,
            okText: vm.$t('Continue') as string,
            cancelText: vm.$t('Cancel') as string,
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
        });
    });
}